import React, { FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { ColumnsType } from "antd/lib/table";
import { Popconfirm, Table, Drawer, Progress, Tooltip, Button, Space } from "antd";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import dayjs from "dayjs";
import { SelectOption } from "../../form-components/SelectComponent";
import { Icon } from "../../../components/Component";
import EditableDependencyTable from "./DependencyForm";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as PhaseIcon } from "../../../assets/icons/PhaseIcon.svg";
import { ReactComponent as ActivityIcon } from "../../../assets/icons/ActivityIcon.svg";
import { ReactComponent as TaskIcon } from "../../../assets/icons/TaskIcon.svg";
import { EditAccess } from "../../../components/accessControl/AccessControl";
import { updatePhaseInDetailModalSlice, resetPhaseInDetailModalSlice } from "../../../store/slices/projectManagement/phase-view/PhaseInDetailView";
import { updateActivityInDetailModalSlice, resetActivityInDetailModalSlice } from "../../../store/slices/projectManagement/activity-view/ActivityInDetailView";
import PhaseQuickViewCard from "../phases/PhaseQuickViewCard";
import ActivityQuickViewCard from "../activity/ActivityQuickViewCard";
import { GenericStatusPill, priorityTone, HIERARCHY_TONES } from "../shared/statusColors";
import { useWbsName, useWbsTreeName } from "../shared/useWbsCodes";

// ---- density-redesign helpers ------------------------------------------------

const initialsOf = (name?: string) => {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

const isDoneStatus = (status?: string) => {
  const s = (status || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return s === "completed" || s === "closed" || s === "approved";
};

// Rolled-up counts for section (phase/activity) rows: total tasks + done.
const countTasks = (node: any): { total: number; done: number } => {
  let total = 0;
  let done = 0;
  (node?.children || []).forEach((child: any) => {
    const key: string = child?.key || "";
    if (key.includes("T-")) {
      total += 1;
      if (isDoneStatus(child?.status)) done += 1;
    }
    const sub = countTasks(child);
    total += sub.total;
    done += sub.done;
  });
  return { total, done };
};

// Keys of expandable rows: top-level parents ("phases" mode works across
// group-by shapes) vs every parent (expand-all).
const collectTopLevelParentKeys = (nodes: any[]): React.Key[] =>
  (nodes || [])
    .filter((n) => n?.children?.length)
    .map((n) => n.key);

const collectAllParentKeys = (nodes: any[]): React.Key[] => {
  const keys: React.Key[] = [];
  const walk = (list: any[]) =>
    (list || []).forEach((n) => {
      if (n?.children?.length) {
        keys.push(n.key);
        walk(n.children);
      }
    });
  walk(nodes || []);
  return keys;
};
interface TaskItem {
  key: string;
  id: number;
  name: string;
  owner: string;
  status?: string;
  priority?: string;
  startDate?: string;
  endDate?: string;
  children?: TaskItem[];
  predecessors?: TaskDependency[];
  successors?: TaskDependency[];
  createdAt?: string;
  plannedStartDate?: string;
  plannedEndDate?: string;
}

export interface TaskDependency {
  taskDependencyId: number;
  dependencyTypeId: number;
  dependencyTypeName: string;
  lagDays: number;
  status: string;
  successorTaskName: string;
  predecessorTaskId: number;
  predecessorTaskName: string;
  predecessorStartDate: string;
  predecessorEndDate: string;
  predecessorDuration: number;
  predecessorOwnerId: number;
  predecessorPriorityName: string;
  predecessorDueDays: {
    days: number;
    hours: number;
    minutes: number;
    isOverdue: boolean;
  };
  successorTaskId: number;
}

const GridView: FC<any> = ({
  projectId,
  gridData,
  taskDropdownData,
  refresh,
  groupBy,
  expandedMode = "phases", // "phases" | "all" — toolbar toggle in TasksListView
  forceExpandAll = false, // true while search/stat-filter is active
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { extra: locationExtra } = (location.state as any) || {};
  const { screenName: locationScreenName } = locationExtra || {};

  // Codes-off labels for phase/activity rows, resolved against sibling groups so
  // an area prefix is only dropped where the row stays unambiguous without it.
  const rowLabel = useWbsTreeName(gridData);
  const wbsName = useWbsName();

  // Drawer state for phase and activity
  const [phaseDrawer, setPhaseDrawer] = useState<{
    open: boolean;
    phase: any | null;
  }>({ open: false, phase: null });

  const [activityDrawer, setActivityDrawer] = useState<{
    open: boolean;
    activity: any | null;
  }>({ open: false, activity: null });

  // Open phase drawer
  const openPhaseDrawer = (record: any) => {
    dispatch(
      updatePhaseInDetailModalSlice({
        open: true,
        id: record?.id,
        phaseName: record?.name,
        activeTab: "view",
        allowEdit: true,
        allowAdd: true,
        projectResponse: record?.projectResponse,
        extra: {
          status: record?.status,
          startDate: record?.startDate,
          endDate: record?.endDate,
          ownerName: record?.owner,
        },
      })
    );
    setPhaseDrawer({ open: true, phase: record });
  };

  // Close phase drawer
  const closePhaseDrawer = () => {
    setPhaseDrawer({ open: false, phase: null });
    dispatch(resetPhaseInDetailModalSlice());
  };

  // Open activity drawer
  const openActivityDrawer = (record: any) => {
    dispatch(
      updateActivityInDetailModalSlice({
        open: true,
        id: record?.id,
        activityName: record?.name,
        activeTab: "view",
        allowEdit: true,
        allowAdd: true,
        projectResponse: record?.projectResponse,
        phaseResponse: record?.phaseResponse,
        extra: {
          status: record?.status,
          startDate: record?.startDate,
          endDate: record?.endDate,
          ownerName: record?.owner,
        },
      })
    );
    setActivityDrawer({ open: true, activity: record });
  };

  // Close activity drawer
  const closeActivityDrawer = () => {
    setActivityDrawer({ open: false, activity: null });
    dispatch(resetActivityInDetailModalSlice());
  };
  const [openPredecessorPopUp, setOpenPredecessorPopUp] = useState<
    number | undefined
  >(undefined);
  const [openSuccessorPopUp, setOpenSuccessorPopUp] = useState<
    number | undefined
  >(undefined);
  const [editPredecessor, setEditPredecessor] = useState(false);
  const [editSuccessor, setEditSuccessor] = useState(false);

  // ---- controlled expansion (progressive disclosure) ----
  const topLevelKeys = useMemo(
    () => collectTopLevelParentKeys(gridData),
    [gridData]
  );
  const allParentKeys = useMemo(
    () => collectAllParentKeys(gridData),
    [gridData]
  );
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  // Reset to the mode's baseline whenever the mode or the tree data changes
  // (refresh, group-by change). Manual caret clicks then diverge from it.
  useEffect(() => {
    setExpandedKeys(expandedMode === "all" ? allParentKeys : topLevelKeys);
  }, [expandedMode, topLevelKeys, allParentKeys]);

  // While a search/stat filter is active, show everything in the (already
  // filtered) tree; the saved expansion state is restored when it clears.
  const effectiveExpandedKeys = forceExpandAll ? allParentKeys : expandedKeys;

  const onRowExpand = (expanded: boolean, record: any) => {
    if (forceExpandAll) return; // don't clobber saved state while forced open
    setExpandedKeys((prev) =>
      expanded
        ? [...prev, record.key]
        : prev.filter((k) => k !== record.key)
    );
  };

  const columns: ColumnsType<TaskItem> = [
    {
      title: "Task",
      dataIndex: "name",
      key: "name",
      // No fixed width / no fixed:"left" — this column flexes to fill the row.
      // A fixed column would force antd's inner horizontal-scroll wrapper, which
      // breaks the outer sticky header (it must pin to .project-content-pane).
      render: (text: any, record: any) => {
        const key: string = record?.key;
        const rollup = !key?.includes("T-") ? countTasks(record) : null;
        const rollupMeta =
          rollup && rollup.total > 0 ? (
            <span className="pms-row-meta">
              {rollup.total} task{rollup.total === 1 ? "" : "s"} · {rollup.done}{" "}
              done
            </span>
          ) : null;

        if (key?.includes("T-")) {
          return (
            <div className="customEditableCell">
              <span
                className="inlineTableCellLink"
                onClick={() => {
                  navigate(`/pms/project/task-detail/${record?.id}`, {
                    state: {
                      ...(location.state as any),
                      projectId: record?.projectResponse?.id,
                      projectName: record?.projectResponse?.name,
                      phaseId: record?.phaseResponse?.id,
                      phaseName: record?.phaseResponse?.name,
                      activityId: record?.activityResponse?.id,
                      activityName: record?.activityResponse?.name,
                      returnPath: `/pms/project/tasks/${record?.projectResponse?.id}`,
                      returnState: {
                        id: record?.projectResponse?.id,
                        projectName: record?.projectResponse?.name,
                      },
                    },
                  });
                }}
              >
                {text}
              </span>
            </div>
          );
        } else if (key?.includes("P-")) {
          // The WBS code and the area prefix both live inside the name string.
          // Codes off = trimmed label; codes on = the full stored name. The raw
          // name stays on hover either way.
          return (
            <div className="customEditableCell">
              <span
                className="inlineTableCellLink"
                onClick={() => openPhaseDrawer(record)}
                style={{
                  fontWeight: 700,
                  fontSize: "13px",
                  color: HIERARCHY_TONES.phase,
                }}
                title={text}
              >
                {rowLabel(key, text)}
              </span>
              {rollupMeta}
            </div>
          );
        } else if (key?.includes("A-")) {
          const phaseName = record?.phaseResponse?.name;
          const showPhaseName = groupBy === "taskList" && phaseName;
          return (
            <div className="customEditableCell">
              <span
                className="inlineTableCellLink"
                onClick={() => openActivityDrawer(record)}
                style={{
                  fontWeight: 600,
                  fontSize: "12px",
                  color: HIERARCHY_TONES.activity,
                }}
                title={text}
              >
                {rowLabel(key, text)}
              </span>
              {rollupMeta}
              {showPhaseName ? (
                <div
                  style={{
                    fontWeight: 400,
                    fontSize: "11px",
                    color: HIERARCHY_TONES.task,
                    marginTop: 2,
                    lineHeight: 1.3,
                  }}
                  title={phaseName}
                >
                  {wbsName(phaseName)}
                </div>
              ) : null}
            </div>
          );
        } else {
          return (
            <div>
              <span>{text}</span>
            </div>
          );
        }
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) =>
        status ? <GenericStatusPill status={status} /> : null,
    },
    {
      title: "Assignee",
      dataIndex: "owner",
      key: "owner",
      width: 76,
      render: (owner: string) =>
        owner ? (
          <Tooltip title={owner}>
            <span className="pms-avatar">{initialsOf(owner)}</span>
          </Tooltip>
        ) : (
          <span style={{ color: "#8094ae" }}>—</span>
        ),
    },
    {
      title: "Timeline",
      key: "timeline",
      width: 180,
      render: (_: any, record: any) => {
        const { startDate, endDate, status, duration } = record || {};
        if (!startDate && !endDate) return null;
        const overdue =
          endDate &&
          !isDoneStatus(status) &&
          dayjs(endDate).isBefore(dayjs(), "day");
        const days =
          duration ??
          (startDate && endDate
            ? dayjs(endDate).diff(dayjs(startDate), "day") + 1
            : null);
        return (
          <Tooltip
            title={`${
              startDate ? dayjs(startDate).format("DD MMM YYYY") : "—"
            } → ${endDate ? dayjs(endDate).format("DD MMM YYYY") : "—"}${
              days != null
                ? ` · Duration: ${days} day${Number(days) === 1 ? "" : "s"}`
                : ""
            }`}
          >
            <span className="pms-timeline-cell">
              {startDate ? dayjs(startDate).format("DD MMM") : "—"}
              <span style={{ color: "#8094ae" }}> → </span>
              <span className={overdue ? "pms-date-overdue" : undefined}>
                {endDate ? dayjs(endDate).format("DD MMM YY") : "—"}
              </span>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Progress",
      dataIndex: "completionPercentage",
      key: "completionPercentage",
      width: 110,
      render: (value: any) => {
        const percent = Number(value);
        if (value === null || value === undefined || isNaN(percent))
          return null;
        return (
          <span className="pms-progress-cell">
            <Progress
              strokeLinecap="butt"
              size={[56, 6]}
              percent={percent}
              showInfo={false}
              strokeColor="#2557d6"
              trailColor="#eef1f6"
            />
            <span className="pms-progress-text">{Math.round(percent)}%</span>
          </span>
        );
      },
    },
    {
      title: "Deps",
      dataIndex: "predecessors",
      key: "dependencies",
      width: 96,
      render: (predecessors: TaskDependency[], record: TaskItem) => {
        const key: string = record?.key;

        if (key?.includes("T-")) {
          predecessors = predecessors?.map((item) => {
            return {
              ...item,
              key: item?.taskDependencyId,
            };
          });
          let successors: TaskDependency[] = (record?.successors || [])?.map(
            (item) => {
              return {
                ...item,
                key: item?.taskDependencyId,
              } as any;
            }
          );

          const depSummary = (
            deps: TaskDependency[],
            nameField: "predecessorTaskName" | "successorTaskName",
            emptyLabel: string
          ) => {
            if (!deps?.length) return emptyLabel;
            const first: any = deps[0] || {};
            const name = first[nameField];
            const { dependencyTypeName, lagDays } = first;
            let t =
              name?.length > 22 ? `${name?.slice(0, 20)}... ` : `${name} `;
            if (deps.length === 1) {
              if (lagDays == 0) t += `(${dependencyTypeName})`;
              else if (lagDays > 0)
                t += `(${dependencyTypeName} +${lagDays} days)`;
              else t += `(${dependencyTypeName} ${lagDays} days)`;
            } else {
              t += ` +${deps.length - 1} more`;
            }
            return t;
          };
          const predTooltip = depSummary(
            predecessors,
            "predecessorTaskName",
            "Add predecessor"
          );
          const succTooltip = depSummary(
            successors,
            "successorTaskName",
            "Add successor"
          );

          const successorTaskIds = record?.successors?.map((item) =>
            item?.successorTaskId?.toString()
          );
          const predecessorsList = taskDropdownData?.filter(
            (item: SelectOption) =>
              !successorTaskIds?.includes(item?.value?.toString()) &&
              item?.value?.toString() != record?.id?.toString()
          );
          const predecessorTaskIds = record?.predecessors?.map((item) =>
            item?.predecessorTaskId?.toString()
          );
          const successorsList = taskDropdownData?.filter(
            (item: SelectOption) =>
              !predecessorTaskIds?.includes(item?.value?.toString()) &&
              item?.value?.toString() != record?.id?.toString()
          );

          return (
            <div className="pms-deps-cell">
              <Popconfirm
                title={<></>}
                icon={<></>}
                showCancel={false}
                open={openPredecessorPopUp === record?.id}
                okButtonProps={{
                  style: {
                    display: "none",
                  },
                }}
                onOpenChange={(visible) => {
                  if (editPredecessor || editSuccessor) return;
                  if (visible) {
                    if (!openPredecessorPopUp && !openSuccessorPopUp) {
                      setOpenPredecessorPopUp(() => record?.id);
                    }
                  }
                }}
                description={
                  <div
                    style={{
                      minWidth: "500px",
                    }}
                  >
                    {editPredecessor ? (
                      <>
                        <EditableDependencyTable
                          moduleKey={"predecessor"}
                          projectId={projectId}
                          predecessorsList={predecessorsList}
                          successorsList={[]}
                          defaultData={predecessors as any}
                          taskId={+record?.id}
                          onRefresh={refresh}
                          onCancel={() => {
                            setEditPredecessor(false);
                            if (predecessors?.length == 0)
                              setOpenPredecessorPopUp(undefined);
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <Table
                          className="customTable"
                          columns={[
                            {
                              title: "Predecessor",
                              dataIndex: "predecessorTaskName",
                              key: "predecessorTaskName",
                            },
                            {
                              title: "Dependency",
                              dataIndex: "dependencyTypeName",
                              key: "dependencyTypeName",
                              width: 50,
                            },
                            {
                              title: "Lag Days",
                              dataIndex: "lagDays",
                              key: "lagDays",
                              width: 80,
                              render: (text: any) => `${text}`,
                            },
                            {
                              title: "Status",
                              dataIndex: "status",
                              key: "status",
                              width: 180,
                              render: (status: string) =>
                                status ? (
                                  <GenericStatusPill status={status} />
                                ) : (
                                  status
                                ),
                            },
                          ]}
                          dataSource={predecessors}
                          pagination={false}
                          bordered
                          size="small"
                        />

                        <Space>
                          <EditAccess moduleName="Tasks">
                            <Button
                              type="primary"
                              size="small"
                              onClick={() => setEditPredecessor(true)}
                              icon={
                                <Icon
                                  name={predecessors?.length > 0 ? "edit" : "add"}
                                />
                              }
                            >
                              {predecessors?.length > 0 ? "Edit" : "Add"}
                            </Button>
                          </EditAccess>
                          <Button
                            size="small"
                            onClick={() => {
                              setEditPredecessor(false);
                              setOpenPredecessorPopUp(undefined);
                            }}
                          >
                            Cancel
                          </Button>
                        </Space>
                      </>
                    )}
                  </div>
                }
              >
                <Tooltip title={predTooltip}>
                  <span
                    className="pms-dep-chip"
                    onClick={() => {
                      if (!predecessors?.length) {
                        setOpenPredecessorPopUp(record?.id);
                        setEditPredecessor(true);
                      }
                    }}
                  >
                    ←{predecessors?.length || "+"}
                  </span>
                </Tooltip>
              </Popconfirm>
              <Popconfirm
                title={<></>}
                icon={<></>}
                showCancel={false}
                open={openSuccessorPopUp === record?.id}
                okButtonProps={{
                  style: {
                    display: "none",
                  },
                }}
                onOpenChange={(visible) => {
                  if (editPredecessor || editSuccessor) return;
                  if (visible) {
                    if (!openPredecessorPopUp && !openSuccessorPopUp) {
                      setOpenSuccessorPopUp(() => record?.id);
                    }
                  }
                }}
                description={
                  <div
                    style={{
                      minWidth: "500px",
                    }}
                  >
                    {editSuccessor ? (
                      <>
                        <EditableDependencyTable
                          moduleKey={"successor"}
                          projectId={projectId}
                          predecessorsList={[]}
                          successorsList={successorsList}
                          defaultData={successors as any}
                          taskId={+record?.id}
                          onRefresh={refresh}
                          onCancel={() => {
                            setEditSuccessor(false);
                            if (successors?.length == 0)
                              setOpenSuccessorPopUp(undefined);
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <Table
                          className="customTable"
                          columns={[
                            {
                              title: "Successor",
                              dataIndex: "successorTaskName",
                              key: "successorTaskName",
                            },
                            {
                              title: "Dependency",
                              dataIndex: "dependencyTypeName",
                              key: "dependencyTypeName",
                              width: 50,
                            },
                            {
                              title: "Lag Days",
                              dataIndex: "lagDays",
                              key: "lagDays",
                              width: 80,
                              render: (text: any) => `${text}`,
                            },
                            {
                              title: "Status",
                              dataIndex: "status",
                              key: "status",
                              width: 80,
                              render: (status: string) =>
                                status ? (
                                  <GenericStatusPill status={status} />
                                ) : (
                                  status
                                ),
                            },
                          ]}
                          dataSource={successors}
                          pagination={false}
                          bordered
                          size="small"
                        />

                        <Space>
                          <EditAccess moduleName="Tasks">
                            <Button
                              type="primary"
                              size="small"
                              onClick={() => setEditSuccessor(true)}
                              icon={
                                <Icon
                                  name={successors?.length > 0 ? "edit" : "add"}
                                />
                              }
                            >
                              {successors?.length > 0 ? "Edit" : "Add"}
                            </Button>
                          </EditAccess>
                          <Button
                            size="small"
                            onClick={() => {
                              setEditSuccessor(false);
                              setOpenSuccessorPopUp(undefined);
                            }}
                          >
                            Cancel
                          </Button>
                        </Space>
                      </>
                    )}
                  </div>
                }
              >
                <Tooltip title={succTooltip}>
                  <span
                    className="pms-dep-chip"
                    onClick={() => {
                      if (!successors?.length) {
                        setOpenSuccessorPopUp(record?.id);
                        setEditSuccessor(true);
                      }
                    }}
                  >
                    →{successors?.length || "+"}
                  </span>
                </Tooltip>
              </Popconfirm>
            </div>
          );
        }
      },
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 72,
      render: (priority: string) =>
        priority ? (
          <Tooltip title={priority}>
            <span
              className="pms-priority-dot"
              style={{ background: priorityTone(priority).dot }}
            />
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <>
      <SkeletonTable
        // loading={loading}
        columns={columns as SkeletonTableColumnsType[]}
      >
        <Table
          className="pms-hub-table customTable taskHierarchyTable"
          columns={columns}
          dataSource={gridData}
          pagination={false}
          size="small"
          tableLayout="fixed"
          // No `scroll` and no fixed column: the table flows naturally so the
          // header can be pinned via pure CSS `position: sticky` to the
          // .project-content-pane scroll container (see project.css).
          expandable={{
            expandedRowKeys: effectiveExpandedKeys,
            onExpand: onRowExpand,
          }}
          rowClassName={(record: any) => {
            const k: string = record?.key || "";
            if (k.includes("P-"))
              return "tasks-row-phase pms-row-phase pms-grid-row";
            if (k.includes("A-"))
              return "tasks-row-activity pms-row-activity pms-grid-row";
            if (k.includes("T-")) return "tasks-row-task pms-grid-row";
            return "pms-grid-row";
          }}
        />
      </SkeletonTable>

      {/* Phase Drawer */}
      <Drawer
        title={`${
          rowLabel(phaseDrawer.phase?.key, phaseDrawer.phase?.name) || "Phase"
        } - Details`}
        placement="right"
        width={720}
        onClose={closePhaseDrawer}
        open={phaseDrawer.open}
        styles={{ body: { padding: 0 } }}
        destroyOnClose
      >
        <PhaseQuickViewCard
          phaseId={phaseDrawer.phase?.id}
          derivedProjectStatus={locationScreenName}
          onClose={closePhaseDrawer}
          onRefresh={refresh}
        />
      </Drawer>

      {/* Activity Drawer */}
      <Drawer
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {activityDrawer.activity?.phaseResponse?.name && (
              <>
                <span style={{ color: "#8094ae", fontWeight: 500 }}>
                  {wbsName(activityDrawer.activity.phaseResponse.name)}
                </span>
                <span style={{ color: "#8094ae", fontWeight: 400 }}>/</span>
              </>
            )}
            <span style={{ color: "#022c57", fontWeight: 600 }}>
              {rowLabel(
                activityDrawer.activity?.key,
                activityDrawer.activity?.name
              ) || "Activity"}
            </span>
            <span style={{ color: "#8094ae", fontWeight: 500 }}>- Details</span>
          </div>
        }
        placement="right"
        width={720}
        onClose={closeActivityDrawer}
        open={activityDrawer.open}
        styles={{ body: { padding: 0 } }}
        destroyOnClose
      >
        <ActivityQuickViewCard
          activityId={activityDrawer.activity?.id}
          derivedProjectStatus={locationScreenName}
          onClose={closeActivityDrawer}
          onRefresh={refresh}
        />
      </Drawer>
    </>
  );
};

export default GridView;
